import apiClient from './client';

/**
 * Price Components API
 * Handles price breakdown by component type and LTA configurations
 */
const priceComponentsApi = {
  /**
   * Get all price components for a part
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @param {number} year - Optional year filter
   * @param {number} month - Optional month filter
   * @returns {Promise<{components: Array}>}
   */
  getComponents: async (partNumber, scenarioName = 'Active', year = null, month = null) => {
    const params = { scenario_name: scenarioName };
    if (year) params.year = year;
    if (month) params.month = month;
    
    const response = await apiClient.get(`/api/v1/parts/${partNumber}/price-components`, { params });
    return response.data.data;
  },

  /**
   * Create or update a price component
   * @param {string} partNumber - Part number
   * @param {Object} componentData - Component data
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  createOrUpdate: async (partNumber, componentData, scenarioName = 'Active') => {
    const response = await apiClient.post(
      `/api/v1/parts/${partNumber}/price-components`,
      componentData,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Delete a price component
   * @param {string} partNumber - Part number
   * @param {string} componentId - Component ID
   * @returns {Promise<Object>}
   */
  delete: async (partNumber, componentId) => {
    const response = await apiClient.delete(
      `/api/v1/parts/${partNumber}/price-components/${componentId}`
    );
    return response.data;
  },

  // ==================== LTA Configuration ====================

  /**
   * Get LTA (Long-Term Agreement) configuration for a part
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>} - { has_lta, lta_config, calculated_reductions }
   */
  getLTAConfig: async (partNumber, scenarioName = 'Active') => {
    const response = await apiClient.get(
      `/api/v1/parts/${partNumber}/lta-config`,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Create or update LTA configuration
   * @param {string} partNumber - Part number
   * @param {Object} ltaData - LTA configuration data
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   * 
   * @example Fixed percent LTA:
   * {
   *   lta_type: "fixed_percent",
   *   fixed_reduction_percent: 3.0,
   *   start_year_offset: 3,
   *   name: "Standard LTA -3%/year"
   * }
   * 
   * @example Custom per-year LTA:
   * {
   *   lta_type: "custom_per_year",
   *   custom_rates: {"2027": 2.0, "2028": 3.0, "2029": 1.5},
   *   name: "Custom LTA Schedule"
   * }
   */
  saveLTAConfig: async (partNumber, ltaData, scenarioName = 'Active') => {
    const response = await apiClient.post(
      `/api/v1/parts/${partNumber}/lta-config`,
      ltaData,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Delete LTA configuration
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  deleteLTAConfig: async (partNumber, scenarioName = 'Active') => {
    const response = await apiClient.delete(
      `/api/v1/parts/${partNumber}/lta-config`,
      { params: { scenario_name: scenarioName } }
    );
    return response.data;
  },

  // ==================== Bulk Operations ====================

  /**
   * Bulk update price components in a single transaction
   * @param {string} partNumber - Part number
   * @param {Array} components - Array of component updates
   * @param {string} scenarioName - Scenario name (default: "Base")
   * @param {boolean} syncForecastData - Whether to sync to ForecastData (default: true)
   * @returns {Promise<Object>}
   * 
   * @example
   * await bulkUpdate('P-00001', [
   *   { year: 2027, month: null, component_type: 'base_material', amount: 8.00 },
   *   { year: 2027, month: null, component_type: 'labor_index', amount: 1.20 },
   * ], 'Base', true);
   */
  bulkUpdate: async (partNumber, components, scenarioName = 'Base', syncForecastData = true) => {
    const response = await apiClient.put(
      `/api/v1/parts/${partNumber}/price-components/bulk`,
      {
        scenario_name: scenarioName,
        components: components,
        sync_forecast_data: syncForecastData
      }
    );
    return response.data;
  }
};

export default priceComponentsApi;
